import { Variables } from '@/services/backstage/config';
import { GraphResponse, useGraph, getUniqueIdentifier, useResource } from '@/services/sidecar/useGraphProvider';
import { DriveItem, ListItem } from '@microsoft/microsoft-graph-types';
import { useVariable } from '@softwareimaging/backstage';
import { useQueryClient } from '@tanstack/react-query';

export enum DirectoryType {
  Folder = 'folder',
  File = 'file',
  Root = 'root'
}

export function objectMap<T, V>(obj: Record<string, T>, fn: (value: T, key: string) => V): Record<string, V> {
  return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, fn(value, key)]));
}

export function objectFilter<T>(obj: Record<string, T>, fn: (value: T, key: string) => boolean) {
  return Object.fromEntries(Object.entries(obj).filter(([key, value]) => fn(value, key)));
}

export interface Directory {
  id: string;
  name: string;
  type: DirectoryType;
  fields?: Record<string, unknown>;
  children?: Directory[];
}

export type List = GraphResponse<
  Pick<
    ListItem & {
      driveItem: DriveItem;
      fields: Record<string, string | string[]>;
    },
    'id' | 'fields' | 'driveItem'
  >[]
>;

export type ItemType = 'folder' | 'file' | 'asset';

export interface Item {
  id: string;
  path: string[];
  name: string;
  directoryId: string;
  createdDateTime: Date;
  type: ItemType;
}

type ParentId = string;
type ItemId = string;

export interface ItemTree {
  id: string;
  type: ItemType;
  createdDateTime: Date;
  items?: ItemTree[];
  path: string[];
}

const getTreeFromItems = (items: Record<string, Item>, lookup: Map<ParentId, ItemId[]>, itemId: string): ItemTree => {
  const item = items[itemId];
  if (!item) throw new Error('Item not found');
  if (item.type === 'file') {
    return {
      id: itemId,
      type: item.type,
      createdDateTime: item.createdDateTime,
      path: item.path
    };
  }

  const children = lookup.get(itemId);

  if (!children) {
    return {
      id: itemId,
      type: item.type,
      createdDateTime: item.createdDateTime,
      path: item.path,
      items: []
    };
  }

  return {
    id: itemId,
    type: item.type,
    createdDateTime: item.createdDateTime,
    path: item.path,
    items: children.map(id => getTreeFromItems(items, lookup, id))
  };
};

export const createExplorerFromList = (listItems: List['value'], ignored: string[] = []) => {
  const interim: Record<string, Omit<Item, 'path' | 'directoryId'>> = {};
  const parentLookup = new Map<ItemId, ParentId>();
  const itemLookup = new Map<ParentId, ItemId[]>();
  const itemsWithoutParents: Set<string> = new Set();

  for (const row of listItems) {
    const item = row.driveItem;
    if (!item || !item.id || !item.name) throw new Error('Missing driveItem');

    if (item.name.endsWith('.sidecar')) continue;

    const parentId = item.parentReference?.id;
    if (!parentId || item.parentReference?.path?.endsWith('root:')) {
      // If there isn't a parent, add it to a list of folders without parents
      // This can be used to reconstruct the directories later
      itemsWithoutParents.add(item.id);
    } else {
      // If there is a parent, add it to both
      // a upwards lookup table, and a downwards lookup table
      // e.g. parent -> [child1, child2, child3], child1 -> parent
      parentLookup.set(item.id, parentId);

      const items = itemLookup.get(parentId) || [];
      items.push(item.id);
      itemLookup.set(parentId, items);
    }

    if (!item.createdDateTime) throw new Error('Missing createdDateTime');

    interim[item.id] = {
      id: item.id,
      name: item.name,
      type: item.folder ? 'folder' : 'file',
      createdDateTime: new Date(item.createdDateTime)
    };
  }
  const items: Record<string, Item> = {};

  for (const itemId in interim) {
    const item = interim[itemId];
    const path: string[] = [];
    let directoryId: string | undefined;
    let pointer: Omit<Item, 'path' | 'directoryId'> = item;

    // Walk the path to get to the root
    // The root is the directoryId
    while (pointer) {
      path.unshift(pointer.id);
      const parentId = parentLookup.get(pointer.id);
      if (!parentId) {
        directoryId = pointer.id;
        break;
      }
      pointer = interim[parentId];
    }

    if (!directoryId) itemsWithoutParents.add(itemId);

    items[itemId] = {
      ...item,
      // Either the item has a parent somewhere in the list, or it's a top level item, so it's directoryId is itself
      directoryId: directoryId ?? itemId,
      path
    };
  }

  // Ignore the ignored folders, i.e. the system folder and the organisation folder
  const notIgnored = Array.from(itemsWithoutParents).filter(id => !ignored.includes(id));
  const directories: Record<string, { items: Record<string, Item>; tree: ItemTree }> = {};
  notIgnored.forEach(id => {
    const inDirectory = objectFilter(items, item => item.directoryId === id);
    directories[id] = {
      items: inDirectory,
      tree: getTreeFromItems(items, itemLookup, id)
    };
  });
  return directories;
};

const useListResource = () => {
  const listId = useVariable<Variables>('listId');
  const siteId = useVariable<Variables>('siteId');
  if (!listId || !siteId) throw new Error('Missing listId or siteId');

  const { client } = useGraph();
  return client
    .api<List>(`/sites/${siteId}/lists/${listId}/items`)
    .expand(['driveItem($select=id,parentReference,name,folder,createdDateTime)', 'fields'])
    .select(['id', 'driveItem', 'fields'])
    .top(1000);
};

export const useInvalidateList = () => {
  const list = useListResource();
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries([getUniqueIdentifier(list)]);
};

export const useList = () => {
  const list = useListResource();
  return useResource(() => list);
};
